import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
// layouts
import DashboardLayout from './layouts/dashboard';
// pages
import Tags from './pages/tags';
import Resource from './pages/resources';
// Legacy `/dashboard/resources/:id` route — the full-page detail view has
// been folded into the right-anchored ResourceSheet (`?detail=:id`). This
// component is a thin Navigate redirect kept around so externally-shared
// links don't 404 during the transition window.
import SeeResourceRedirect from './pages/resources/see-resource';
import Items from './pages/items';
import Flows from './pages/flows';
import Login from './pages/login';
import ForgotPassword from './pages/forgot-password';
import AcceptInvitation from './pages/accept-invitation';
import ResetPasswordAccept from './pages/reset-password-accept';
import Reset2FA from './pages/reset-2fa';
import Reset2FAAccept from './pages/reset-2fa-accept';
import Setup2FA from './pages/setup-2fa';
import NotFound from './pages/page-404';
import DashboardApp from './pages/dashboard';
import Credits from './pages/credits';
import Integrations from './pages/integrations';
import User from './pages/users';
import Permissions from './pages/permissions';
import Inspections from './pages/inspections';
import Insights from './pages/insights';
import SeeInspection from './pages/inspections/see-inspection';
import Search from './pages/search';
import LocalStorage from "./utils/storage";

// Dev-only design-system reference. The lazy() + dynamic import sits
// behind an import.meta.env.DEV ternary so Rollup eliminates the whole
// branch (and never emits the chunk) in production builds — leaving the
// lazy call unconditional would still produce a referenced chunk.
const DesignSystem = import.meta.env.DEV
  ? lazy(() => import('./pages/design-system'))
  : null;

// ----------------------------------------------------------------------
const STORAGE_KEY_PREFIX = 'session_';

function loadSessionFromStorage(storage) {
  const key = `${STORAGE_KEY_PREFIX}current`;
  return storage.get(key, true);
}

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired
};

function RequireAuth({ children }) {
  const authed = useSelector((state) => !!state.session.current) || loadSessionFromStorage(LocalStorage) !== null;
  return authed === true ? children : <Navigate to="/login" replace />;
}

export default function Router() {
  const routes = useRoutes([
    {
      path: '/dashboard',
      element: (
        <RequireAuth>
          <DashboardLayout />
        </RequireAuth>
      ),
      children: [
        { path: 'app', element: <DashboardApp /> },
        { path: 'search', element: <Search /> },
        { path: 'inspections', element: <Inspections /> },
        { path: 'insights', element: <Insights /> },
        { path: 'inspections/:id', element: <SeeInspection /> },
        { path: 'tags', element: <Tags /> },
        { path: 'resources', element: <Resource /> },
        { path: 'resources/:id', element: <SeeResourceRedirect /> },
        { path: 'items', element: <Items /> },
        { path: 'users', element: <User /> },
        { path: 'permissions', element: <Permissions /> },
        { path: 'flows', element: <Flows /> },
        { path: 'credits', element: <Credits /> },
        { path: 'integrations', element: <Integrations /> },
      ],
    },
    { path: '/login', element: <Login /> },
    { path: '/forgot-password', element: <ForgotPassword /> },
    { path: '/accept-invitation', element: <AcceptInvitation /> },
    { path: '/reset-password', element: <ResetPasswordAccept /> },
    { path: '/reset-2fa', element: <Reset2FA /> },
    { path: '/reset-2fa-accept', element: <Reset2FAAccept /> },
    { path: '/setup-2fa', element: <Setup2FA /> },
    // Dev-only design-system reference: gated so it doesn't ship publicly.
    ...(import.meta.env.DEV
      ? [{
        path: '/design-system',
        element: (
          <Suspense fallback={null}>
            <DesignSystem />
          </Suspense>
        ),
      }]
      : []),
    { path: '/404', element: <NotFound /> },
    { path: '/', element: <Navigate to="/dashboard/app" /> },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
  
  return routes;
}